'use client';

import { c as _c } from "react/compiler-runtime";
import type { GithubFile } from '@/types/github';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { ChatRequestOptions } from 'ai';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUp, Square } from 'lucide-react';
import type React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import getCaretCoordinates from 'textarea-caret';
import { useDebounceCallback, useLocalStorage, useWindowSize } from 'usehooks-ts';
import type { AiContext } from './types/ai-context';
import type { SidebarChatMessage } from './types/sidebar-chat-message';
import { buildSidebarChatModelBody } from './types/chat-request';
import { PromptInput, PromptInputBody, PromptInputFooter, PromptInputTools } from '@/components/ai-elements/prompt-input';
import { CHAT_COMPOSER_BOX_CLASS } from '@/components/chat/chat-composer-skeleton';
import { buildChatDraftStorageKey } from '@/components/chat/chat-input-utils';
import { ChatModelSelector } from '@/components/chat/chat-model-selector';
import { ChatSuggestionRow } from '@/components/chat/chat-suggestion-row';
import { CHAT_SUGGESTION_LIST_CLASS, CHAT_SUGGESTIONS_CONTENT_INSET_CLASS, CHAT_SUGGESTIONS_LAYER_OUTER_CLASS, ChatSuggestionsSkeleton } from '@/components/chat/chat-suggestions-skeleton';
import { PrV2VisibleFileIndicator } from '@/components/chat/chat-visible-file-indicator';
import { MentionableFilesDropdown } from '@/components/chat/mentionable-files-dropdown';
import { useChatModelSelection } from '@/components/chat/use-chat-model-selection';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { BetterTooltip } from '@/components/ui/tooltip';
import { buildChatSuggestionsContext, useChatSuggestions } from '@/data/chat/use-chat-suggestions';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { TOUR_MIN_ORDERED_FILES } from '@/lib/chat/tour-protocol';
import { useChatStore } from '@/lib/stores/chat-store';
import { getPrV2ActiveFileStoreKey } from '@/lib/stores/navigation/pr-v2-active-file-store';
import { usePrV2ContentPainted } from '@/lib/stores/navigation/pr-v2-content-paint-store';
import { usePrStore } from '@/lib/stores/pr-store';
import { FALLBACK_SUGGESTIONS, isNavigationSuggestion, TOUR_SUGGESTION } from '@/lib/suggestions';
import { cn } from '@/lib/utils';
import { sanitizeUIMessages } from '@/utils/chat';
const DESKTOP_WIDTH_BREAKPOINT_PX = 768;
// Typed in the chat box, starts a new chat. The box has no other control for it.
const NEW_CHAT_COMMAND = '/new';
const DEFAULT_FILES: GithubFile[] = [];
type QueuedPromptInput = {
  modelId: string;
  text: string;
};

/**
 * A one-line status just above the composer box, as tall as `suggestionsLayerClass` lifts the chips.
 * It starts at the box's left edge, in line with the chip icons and the column's text above it.
 */
function ComposerStatusLine(t0) {
  const $ = _c(2);
  const {
    status
  } = t0;
  if (!status) {
    return null;
  }
  let t1;
  if ($[0] !== status) {
    t1 = <div role="status" className="flex h-6 min-w-0 items-start">{status}</div>;
    $[0] = status;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/** The suggestion chips sit just above the composer, and above its status line's h-6 when it shows. */
function suggestionsLayerClass(status: React.ReactNode): string {
  return status ? cn(CHAT_SUGGESTIONS_LAYER_OUTER_CLASS, 'bottom-46') : CHAT_SUGGESTIONS_LAYER_OUTER_CLASS;
}
export function MultimodalInput(t0) {
  const $ = _c(163);
  const {
    chatId,
    input,
    setInput,
    isLoading,
    stop,
    setMessages,
    sendMessage,
    queuePrompt,
    queueContent,
    className,
    aiContext,
    files: t1,
    conversationEmpty,
    tools,
    status
  } = t0;
  const files = t1 === undefined ? DEFAULT_FILES : t1;
  const textareaRef = useRef(null);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const {
    width
  } = useWindowSize();
  const isDesktopWidth = Boolean(width && width >= DESKTOP_WIDTH_BREAKPOINT_PX);
  const setChatId = useChatStore(_temp);
  const startNewChatInStore = useChatStore(_temp2);
  const {
    owner,
    repo,
    number,
    installationId,
    isInitialized: isPrStoreReady
  } = usePrStore();
  let t2;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t2 = getPrV2ActiveFileStoreKey(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hasContentPainted = usePrV2ContentPainted(t2);
  const isPullRequestStale = aiContext.pr.repository.owner !== owner || aiContext.pr.repository.name !== repo || String(aiContext.pr.number) !== number;
  let t3;
  if ($[4] !== aiContext) {
    t3 = buildChatSuggestionsContext(aiContext);
    $[4] = aiContext;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const suggestionContext = t3;
  const shouldFetchSuggestions = isPrStoreReady && !isPullRequestStale && hasContentPainted && Boolean(installationId) && conversationEmpty;
  const t4 = installationId ?? 0;
  const t5 = aiContext.headSha ?? aiContext.pr.head.sha;
  let t6;
  if ($[6] !== aiContext.pr.number || $[7] !== suggestionContext || $[8] !== t4 || $[9] !== t5) {
    t6 = {
      context: suggestionContext,
      installationId: t4,
      prNumber: aiContext.pr.number,
      commitHash: t5
    };
    $[6] = aiContext.pr.number;
    $[7] = suggestionContext;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== shouldFetchSuggestions) {
    t7 = {
      enabled: shouldFetchSuggestions
    };
    $[11] = shouldFetchSuggestions;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const {
    data: suggestedActions,
    isLoading: isFetchingSuggestions
  } = useChatSuggestions(t6, t7);
  const baseSuggestions = suggestedActions?.length ? suggestedActions : FALLBACK_SUGGESTIONS;
  const isTourEligible = (aiContext.orderedFiles?.length ?? 0) >= TOUR_MIN_ORDERED_FILES;
  let t8;
  if ($[13] !== baseSuggestions || $[14] !== isTourEligible) {
    t8 = isTourEligible ? [...baseSuggestions.slice(0, 2), TOUR_SUGGESTION] : baseSuggestions;
    $[13] = baseSuggestions;
    $[14] = isTourEligible;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const finalSuggestions = t8;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 2}px`;
      }
    };
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const adjustHeight = t9;
  let t10;
  if ($[17] !== chatId) {
    t10 = buildChatDraftStorageKey(chatId);
    $[17] = chatId;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const draftStorageKey = t10;
  const [, setLocalStorageInput] = useLocalStorage(draftStorageKey, "");
  const {
    canSelectChatModel,
    effectiveSelectedModel,
    isModelSelectorOpen,
    modelsByProvider,
    selectedModel,
    selectedModelInfo,
    setIsModelSelectorOpen,
    setSelectedModel
  } = useChatModelSelection(owner);
  const [showFilesDropdown, setShowFilesDropdown] = useState(false);
  const [fileSearchQuery, setFileSearchQuery] = useState("");
  const [fileMentionStartPosition, setFileMentionStartPosition] = useState(null);
  const [activeMentionOptionId, setActiveMentionOptionId] = useState(undefined);
  const [dropdownCoordinates, setDropdownCoordinates] = useState(null);
  const mentionableFilesListboxId = useId();
  let t11;
  if ($[19] !== input || $[20] !== setLocalStorageInput) {
    t11 = () => {
      setLocalStorageInput(input);
      if (textareaRef.current) {
        adjustHeight();
      }
    };
    $[19] = input;
    $[20] = setLocalStorageInput;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== draftStorageKey || $[23] !== input || $[24] !== setLocalStorageInput) {
    t12 = [draftStorageKey, input, setLocalStorageInput];
    $[22] = draftStorageKey;
    $[23] = input;
    $[24] = setLocalStorageInput;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  useEffect(t11, t12);
  let t13;
  if ($[26] !== fileMentionStartPosition || $[27] !== files || $[28] !== setInput || $[29] !== showFilesDropdown) {
    t13 = event => {
      const newValue = event.target.value;
      const cursorPosition = event.target.selectionStart;
      if (cursorPosition > 0 && newValue[cursorPosition - 1] === "@") {
        const textarea = textareaRef.current;
        if (textarea && files && files.length > 0) {
          const coords = getCaretCoordinates(textarea, cursorPosition);
          const textareaRect = textarea.getBoundingClientRect();
          setDropdownCoordinates({
            left: textareaRect.left + coords.left,
            top: textareaRect.top + coords.top + coords.height
          });
          setShowFilesDropdown(true);
          setFileMentionStartPosition(cursorPosition - 1);
          setFileSearchQuery("@");
        }
      } else {
        if (showFilesDropdown && fileMentionStartPosition !== null) {
          const searchText = newValue.slice(fileMentionStartPosition, cursorPosition);
          if (searchText.startsWith("@") && !/\s/.test(searchText)) {
            setFileSearchQuery(searchText);
          } else {
            setShowFilesDropdown(false);
            setFileMentionStartPosition(null);
          }
        }
      }
      setInput(newValue);
      adjustHeight();
    };
    $[26] = fileMentionStartPosition;
    $[27] = files;
    $[28] = setInput;
    $[29] = showFilesDropdown;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const handleInput = t13;
  const [showSuggestionsLoading, setShowSuggestionsLoading] = useState(false);
  let t14;
  if ($[31] !== fileMentionStartPosition || $[32] !== isDesktopWidth || $[33] !== setInput) {
    t14 = file => {
      if (!textareaRef.current || fileMentionStartPosition === null) {
        return;
      }
      if (!file.filename) {
        setShowFilesDropdown(false);
        setFileMentionStartPosition(null);
        return;
      }
      const currentValue = textareaRef.current.value;
      const beforeMention = currentValue.slice(0, fileMentionStartPosition);
      const afterMention = currentValue.slice(textareaRef.current.selectionStart);
      const shouldAddSpaceAfterMention = afterMention.length > 0 && !/^\s/.test(afterMention);
      const mentionSuffix = shouldAddSpaceAfterMention ? " " : "";
      const newValue_0 = `${beforeMention}@${file.filename}${mentionSuffix}${afterMention}`;
      setInput(newValue_0);
      setShowFilesDropdown(false);
      setFileMentionStartPosition(null);
      const newCursorPosition = fileMentionStartPosition + file.filename.length + 1 + mentionSuffix.length;
      if (textareaRef.current) {
        if (isDesktopWidth) {
          textareaRef.current.focus();
        }
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    };
    $[31] = fileMentionStartPosition;
    $[32] = isDesktopWidth;
    $[33] = setInput;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const handleFileMentionSelect = t14;
  const debouncedSetSuggestionsLoading = useDebounceCallback(setShowSuggestionsLoading, 200);
  let t15;
  let t16;
  if ($[35] !== debouncedSetSuggestionsLoading || $[36] !== isFetchingSuggestions) {
    t15 = () => {
      if (isFetchingSuggestions) {
        debouncedSetSuggestionsLoading(true);
      } else {
        debouncedSetSuggestionsLoading.cancel();
        setShowSuggestionsLoading(false);
      }
      return () => {
        debouncedSetSuggestionsLoading.cancel();
      };
    };
    t16 = [isFetchingSuggestions, debouncedSetSuggestionsLoading];
    $[35] = debouncedSetSuggestionsLoading;
    $[36] = isFetchingSuggestions;
    $[37] = t15;
    $[38] = t16;
  } else {
    t15 = $[37];
    t16 = $[38];
  }
  useEffect(t15, t16);
  let t17;
  if ($[39] !== chatId || $[40] !== effectiveSelectedModel || $[41] !== input || $[42] !== isDesktopWidth || $[43] !== sendMessage || $[44] !== setChatId || $[45] !== setInput || $[46] !== setLocalStorageInput) {
    t17 = () => {
      setChatId(chatId);
      if (input.trim()) {
        sendMessage({
          text: input
        }, {
          body: buildSidebarChatModelBody(effectiveSelectedModel)
        });
        setInput("");
        setLocalStorageInput("");
        if (isDesktopWidth) {
          textareaRef.current?.focus();
        }
      }
    };
    $[39] = chatId;
    $[40] = effectiveSelectedModel;
    $[41] = input;
    $[42] = isDesktopWidth;
    $[43] = sendMessage;
    $[44] = setChatId;
    $[45] = setInput;
    $[46] = setLocalStorageInput;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  const submitForm = t17;
  let t18;
  if ($[48] !== chatId || $[49] !== effectiveSelectedModel || $[50] !== input || $[51] !== isDesktopWidth || $[52] !== queuePrompt || $[53] !== setChatId || $[54] !== setInput || $[55] !== setLocalStorageInput) {
    t18 = () => {
      if (!input.trim()) {
        return;
      }
      setChatId(chatId);
      queuePrompt({
        text: input,
        modelId: effectiveSelectedModel
      });
      setInput("");
      setLocalStorageInput("");
      if (isDesktopWidth) {
        textareaRef.current?.focus();
      }
    };
    $[48] = chatId;
    $[49] = effectiveSelectedModel;
    $[50] = input;
    $[51] = isDesktopWidth;
    $[52] = queuePrompt;
    $[53] = setChatId;
    $[54] = setInput;
    $[55] = setLocalStorageInput;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  const queueCurrentPrompt = t18;
  let t19;
  if ($[57] !== input) {
    t19 = input.trim();
    $[57] = input;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  const hasInput = t19.length > 0;
  let t20;
  if ($[59] !== input) {
    t20 = input.trim().toLowerCase();
    $[59] = input;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  const isNewChatCommand = t20 === NEW_CHAT_COMMAND;
  let t21;
  if ($[61] !== setInput || $[62] !== setLocalStorageInput || $[63] !== startNewChatInStore || $[64] !== stop) {
    t21 = () => {
      stop();
      setInput("");
      setLocalStorageInput("");
      startNewChatInStore();
    };
    $[61] = setInput;
    $[62] = setLocalStorageInput;
    $[63] = startNewChatInStore;
    $[64] = stop;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  const startNewChat = t21;
  let t22;
  if ($[66] !== hasInput || $[67] !== isLoading || $[68] !== isNewChatCommand || $[69] !== queueCurrentPrompt || $[70] !== startNewChat || $[71] !== submitForm) {
    t22 = () => {
      if (isNewChatCommand) {
        startNewChat();
      } else {
        if (isLoading) {
          if (hasInput) {
            queueCurrentPrompt();
          }
        } else {
          submitForm();
        }
      }
    };
    $[66] = hasInput;
    $[67] = isLoading;
    $[68] = isNewChatCommand;
    $[69] = queueCurrentPrompt;
    $[70] = startNewChat;
    $[71] = submitForm;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  const submitInput = t22;
  const isAwaitingSuggestions = !suggestedActions && conversationEmpty && (!hasContentPainted || isFetchingSuggestions);
  const showSkeleton = showSuggestionsLoading || !isPrStoreReady || isPullRequestStale || isAwaitingSuggestions;
  let t23;
  if ($[73] !== aiContext.orderedFiles?.length || $[74] !== chatId || $[75] !== conversationEmpty || $[76] !== effectiveSelectedModel || $[77] !== finalSuggestions || $[78] !== hasInput || $[79] !== installationId || $[80] !== number || $[81] !== owner || $[82] !== repo || $[83] !== sendMessage || $[84] !== setChatId || $[85] !== showSkeleton || $[86] !== status) {
    t23 = conversationEmpty && !hasInput && <div data-testid="chat-suggestions-layer" className={suggestionsLayerClass(status)}><AnimatePresence mode="wait">{showSkeleton ? <motion.div key="loading" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} className="pointer-events-auto w-full"><ChatSuggestionsSkeleton /></motion.div> : <motion.div key="suggestions" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} className={cn("pointer-events-auto w-full", CHAT_SUGGESTIONS_CONTENT_INSET_CLASS)}><div className={CHAT_SUGGESTION_LIST_CLASS}>{finalSuggestions.map((suggestedAction, suggestionIndex) => {
              const kind = isNavigationSuggestion(suggestedAction) ? "navigate" : "question";
              const isTour = suggestedAction.title === TOUR_SUGGESTION.title;
              const isFallback = FALLBACK_SUGGESTIONS.some(fallback => fallback.title === suggestedAction.title);
              return <ChatSuggestionRow key={`${suggestionIndex}-${suggestedAction.title}`} kind={isTour ? "tour" : kind} label={suggestedAction.title} data-suggestion-kind={kind} onClick={async () => {
                setChatId(chatId);
                trackClientEvent("v2_chat_suggestion_clicked", installationId?.toString(), {
                  owner,
                  repo,
                  pr_number: number,
                  suggestion_index: suggestionIndex,
                  suggestion_title: suggestedAction.title,
                  suggestion_kind: kind,
                  suggestion_type: isTour ? "tour" : isFallback ? "fallback" : "generated",
                  is_fallback: isFallback
                });
                if (isTour) {
                  trackClientEvent("v2_chat_tour_started", installationId?.toString(), {
                    owner,
                    repo,
                    pr_number: number,
                    stop_count: aiContext.orderedFiles?.length ?? 0
                  });
                }
                sendMessage({
                  text: suggestedAction.action
                }, {
                  body: buildSidebarChatModelBody(effectiveSelectedModel)
                });
              }} />;
            })}</div></motion.div>}</AnimatePresence></div>;
    $[73] = aiContext.orderedFiles?.length;
    $[74] = chatId;
    $[75] = conversationEmpty;
    $[76] = effectiveSelectedModel;
    $[77] = finalSuggestions;
    $[78] = hasInput;
    $[79] = installationId;
    $[80] = number;
    $[81] = owner;
    $[82] = repo;
    $[83] = sendMessage;
    $[84] = setChatId;
    $[85] = showSkeleton;
    $[86] = status;
    $[87] = t23;
  } else {
    t23 = $[87];
  }
  let t24;
  if ($[88] !== status) {
    t24 = <ComposerStatusLine status={status} />;
    $[88] = status;
    $[89] = t24;
  } else {
    t24 = $[89];
  }
  const t25 = queueContent ? "rounded-t-none border-t-0" : "rounded-sm";
  let t26;
  if ($[90] !== t25) {
    t26 = cn(CHAT_COMPOSER_BOX_CLASS, t25);
    $[90] = t25;
    $[91] = t26;
  } else {
    t26 = $[91];
  }
  let t27;
  if ($[92] !== aiContext) {
    t27 = <PrV2VisibleFileIndicator aiContext={aiContext} className="relative -mb-1.5 self-start px-4 pt-2.5" />;
    $[92] = aiContext;
    $[93] = t27;
  } else {
    t27 = $[93];
  }
  const t28 = isTextareaFocused ? `@ to mention files · ${NEW_CHAT_COMMAND} for a new chat` : "Ask about this PR\u2026";
  let t29;
  let t30;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => setIsTextareaFocused(true);
    t30 = () => setIsTextareaFocused(false);
    $[94] = t29;
    $[95] = t30;
  } else {
    t29 = $[94];
    t30 = $[95];
  }
  let t31;
  if ($[96] !== className) {
    t31 = cn("min-h-[44px] max-h-[calc(30dvh)] overflow-y-auto resize-none rounded-none border-none bg-transparent py-3 pl-2 pr-3 text-sm shadow-none transition-all duration-200 focus-visible:ring-0", className);
    $[96] = className;
    $[97] = t31;
  } else {
    t31 = $[97];
  }
  let t32;
  if ($[98] !== showFilesDropdown || $[99] !== submitInput) {
    t32 = event_0 => {
      if (showFilesDropdown && (event_0.key === "Enter" || event_0.key === "Escape")) {
        return;
      }
      if (event_0.key === "Enter" && !event_0.shiftKey) {
        event_0.preventDefault();
        submitInput();
      } else {
        if (event_0.key === "Escape") {
          event_0.currentTarget.blur();
        }
      }
    };
    $[98] = showFilesDropdown;
    $[99] = submitInput;
    $[100] = t32;
  } else {
    t32 = $[100];
  }
  const t33 = showFilesDropdown ? mentionableFilesListboxId : undefined;
  const t34 = showFilesDropdown ? activeMentionOptionId : undefined;
  let t35;
  if ($[101] !== handleInput || $[102] !== input || $[103] !== showFilesDropdown || $[104] !== t28 || $[105] !== t31 || $[106] !== t32 || $[107] !== t33 || $[108] !== t34) {
    t35 = <PromptInputBody><Textarea ref={textareaRef} data-chat-input="true" placeholder={t28} value={input} onChange={handleInput} onFocus={t29} onBlur={t30} className={t31} rows={2} autoFocus={false} onKeyDown={t32} suppressHydrationWarning={true} aria-controls={t33} aria-activedescendant={t34} aria-expanded={showFilesDropdown} aria-autocomplete="list" aria-haspopup="listbox" /></PromptInputBody>;
    $[101] = handleInput;
    $[102] = input;
    $[103] = showFilesDropdown;
    $[104] = t28;
    $[105] = t31;
    $[106] = t32;
    $[107] = t33;
    $[108] = t34;
    $[109] = t35;
  } else {
    t35 = $[109];
  }
  let t36;
  if ($[110] !== canSelectChatModel || $[111] !== isLoading || $[112] !== isModelSelectorOpen || $[113] !== modelsByProvider || $[114] !== selectedModel || $[115] !== selectedModelInfo || $[116] !== setIsModelSelectorOpen || $[117] !== setSelectedModel) {
    t36 = <ChatModelSelector canSelectChatModel={canSelectChatModel} isLoading={isLoading} isModelSelectorOpen={isModelSelectorOpen} modelsByProvider={modelsByProvider} selectedModel={selectedModel} selectedModelInfo={selectedModelInfo} setIsModelSelectorOpen={setIsModelSelectorOpen} setSelectedModel={setSelectedModel} />;
    $[110] = canSelectChatModel;
    $[111] = isLoading;
    $[112] = isModelSelectorOpen;
    $[113] = modelsByProvider;
    $[114] = selectedModel;
    $[115] = selectedModelInfo;
    $[116] = setIsModelSelectorOpen;
    $[117] = setSelectedModel;
    $[118] = t36;
  } else {
    t36 = $[118];
  }
  let t37;
  if ($[119] !== t36 || $[120] !== tools) {
    t37 = <PromptInputTools>{t36}{tools}</PromptInputTools>;
    $[119] = t36;
    $[120] = tools;
    $[121] = t37;
  } else {
    t37 = $[121];
  }
  let t38;
  if ($[122] !== hasInput || $[123] !== isLoading || $[124] !== setMessages || $[125] !== stop || $[126] !== submitInput) {
    t38 = <div className="ml-auto flex shrink-0 gap-1">{isLoading ? <BetterTooltip content="Stop generating" side="top"><Button className="h-6 w-6 rounded-md bg-muted-foreground/10 text-foreground hover:bg-muted-foreground/20 transition-all duration-200 shadow-sm" onClick={event_1 => {
          event_1.preventDefault();
          stop();
          setMessages(_temp3);
        }} size="icon"><Square size={14} /></Button></BetterTooltip> : <BetterTooltip content={!hasInput ? "Type a message first" : "Send message"} side="top"><Button className={cn("h-6 w-6 rounded-md transition-all duration-200 shadow-sm", !hasInput ? "bg-muted-foreground/20 text-muted-foreground cursor-not-allowed" : "bg-muted-foreground/10 text-foreground hover:bg-muted-foreground/20")} onClick={event_2 => {
          event_2.preventDefault();
          submitInput();
        }} disabled={!hasInput} size="icon"><ArrowUp size={12} /></Button></BetterTooltip>}</div>;
    $[122] = hasInput;
    $[123] = isLoading;
    $[124] = setMessages;
    $[125] = stop;
    $[126] = submitInput;
    $[127] = t38;
  } else {
    t38 = $[127];
  }
  let t39;
  if ($[128] !== t37 || $[129] !== t38) {
    t39 = <PromptInputFooter>{t37}{t38}</PromptInputFooter>;
    $[128] = t37;
    $[129] = t38;
    $[130] = t39;
  } else {
    t39 = $[130];
  }
  let t40;
  if ($[131] !== t26 || $[132] !== t27 || $[133] !== t35 || $[134] !== t39) {
    t40 = <PromptInput className={t26}>{t27}{t35}{t39}</PromptInput>;
    $[131] = t26;
    $[132] = t27;
    $[133] = t35;
    $[134] = t39;
    $[135] = t40;
  } else {
    t40 = $[135];
  }
  let t41;
  if ($[136] !== queueContent || $[137] !== t24 || $[138] !== t40) {
    t41 = <div className="relative">{t24}{queueContent}{t40}</div>;
    $[136] = queueContent;
    $[137] = t24;
    $[138] = t40;
    $[139] = t41;
  } else {
    t41 = $[139];
  }
  const t42 = `${(dropdownCoordinates?.left ?? 0) - 28}px`;
  const t43 = `${dropdownCoordinates?.top ?? 0}px`;
  let t44;
  if ($[140] !== t42 || $[141] !== t43) {
    t44 = <PopoverPrimitive.Anchor asChild={true}><div className="fixed pointer-events-none" style={{
        left: t42,
        top: t43,
        width: 1,
        height: 1
      }} /></PopoverPrimitive.Anchor>;
    $[140] = t42;
    $[141] = t43;
    $[142] = t44;
  } else {
    t44 = $[142];
  }
  const t45 = showFilesDropdown ? "animate-in fade-in-0 zoom-in-95" : "animate-out fade-out-0 zoom-out-95";
  let t46;
  if ($[143] !== t45) {
    t46 = cn("w-[300px] p-0 outline-hidden", "transition-all duration-100", t45, "data-[side=top]:-translate-y-7", "z-100");
    $[143] = t45;
    $[144] = t46;
  } else {
    t46 = $[144];
  }
  let t47;
  if ($[145] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = () => {
      setShowFilesDropdown(false);
      setFileMentionStartPosition(null);
    };
    $[145] = t47;
  } else {
    t47 = $[145];
  }
  let t48;
  if ($[146] !== fileSearchQuery || $[147] !== files || $[148] !== handleFileMentionSelect || $[149] !== mentionableFilesListboxId || $[150] !== showFilesDropdown) {
    t48 = showFilesDropdown && files && files.length > 0 ? <MentionableFilesDropdown files={files} searchQuery={fileSearchQuery} onSelect={handleFileMentionSelect} listboxId={mentionableFilesListboxId} onActiveOptionIdChange={setActiveMentionOptionId} /> : showFilesDropdown && <div className="p-4 text-sm">No files available</div>;
    $[146] = fileSearchQuery;
    $[147] = files;
    $[148] = handleFileMentionSelect;
    $[149] = mentionableFilesListboxId;
    $[150] = showFilesDropdown;
    $[151] = t48;
  } else {
    t48 = $[151];
  }
  let t49;
  if ($[152] !== t46 || $[153] !== t48) {
    t49 = <PopoverPrimitive.Portal><PopoverPrimitive.Content className={t46} side="bottom" align="start" sideOffset={0} alignOffset={0} collisionPadding={16} avoidCollisions={true} onOpenAutoFocus={_temp4} onCloseAutoFocus={_temp5} onEscapeKeyDown={_temp6} onPointerDownOutside={t47} forceMount={true}>{t48}</PopoverPrimitive.Content></PopoverPrimitive.Portal>;
    $[152] = t46;
    $[153] = t48;
    $[154] = t49;
  } else {
    t49 = $[154];
  }
  let t50;
  if ($[155] !== showFilesDropdown || $[156] !== t44 || $[157] !== t49) {
    t50 = <PopoverPrimitive.Root open={showFilesDropdown} modal={false}>{t44}{t49}</PopoverPrimitive.Root>;
    $[155] = showFilesDropdown;
    $[156] = t44;
    $[157] = t49;
    $[158] = t50;
  } else {
    t50 = $[158];
  }
  let t51;
  if ($[159] !== t23 || $[160] !== t41 || $[161] !== t50) {
    t51 = <div className="relative flex w-full flex-col gap-4">{t23}{t41}{t50}</div>;
    $[159] = t23;
    $[160] = t41;
    $[161] = t50;
    $[162] = t51;
  } else {
    t51 = $[162];
  }
  return t51;
}
function _temp6(e_1) {
  e_1.preventDefault();
}
function _temp5(e_0) {
  return e_0.preventDefault();
}
function _temp4(e) {
  return e.preventDefault();
}
function _temp3(messages) {
  return sanitizeUIMessages(messages);
}
function _temp2(state_0) {
  return state_0.startNewChat;
}
function _temp(state) {
  return state.setChatId;
}